import { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import { productsAPI, categoriesAPI, suppliersAPI, barcodeAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { BarcodeScanner } from '../components/BarcodeScanner';
import { toast } from 'sonner';
import { Plus, Search, MoreVertical, Pencil, Trash2, Package, Loader2, RefreshCw, AlertTriangle, Tag, LayoutGrid, LayoutList, DollarSign, TrendingUp, Scan, CheckCircle } from 'lucide-react';

export default function Products() {
    const { user } = useAuth();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [saving, setSaving] = useState(false);
    const [viewMode, setViewMode] = useState('table');

    const [form, setForm] = useState({
        name: '', sku: '', barcode: '', description: '', category_id: '',
        price: '', cost_price: '', stock_quantity: '', low_stock_threshold: '10',
        image_url: '', auto_reorder: false, reorder_supplier_id: '', reorder_quantity: '20'
    });
    const [categoryForm, setCategoryForm] = useState({ name: '', description: '' });
    const [scannerOpen, setScannerOpen] = useState(false);
    const [lookingUp, setLookingUp] = useState(false);
    const [barcodeLookupResult, setBarcodeLookupResult] = useState(null);

    useEffect(() => { if (user?.store_id) fetchData(); else setLoading(false); }, [user]);

    const fetchData = async () => {
        try {
            const [productsRes, categoriesRes, suppliersRes] = await Promise.all([
                productsAPI.getAll(), categoriesAPI.getAll(), suppliersAPI.getAll()
            ]);
            setProducts(productsRes.data);
            setCategories(categoriesRes.data);
            setSuppliers(suppliersRes.data);
        } catch { toast.error('Failed to load products'); }
        finally { setLoading(false); }
    };

    const filteredProducts = products.filter(p => {
        const matchSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.sku.toLowerCase().includes(searchQuery.toLowerCase());
        const matchCat = filterCategory === 'all' || p.category_id === filterCategory;
        return matchSearch && matchCat;
    });

    const lowStockCount = products.filter(p => p.stock_quantity <= p.low_stock_threshold).length;
    const inventoryValue = products.reduce((s, p) => s + p.price * p.stock_quantity, 0);

    const resetForm = () => {
        setForm({ name: '', sku: '', barcode: '', description: '', category_id: '', price: '', cost_price: '', stock_quantity: '', low_stock_threshold: '10', image_url: '', auto_reorder: false, reorder_supplier_id: '', reorder_quantity: '20' });
        setEditingProduct(null);
    };

    const handleOpenDialog = (product = null) => {
        if (product) {
            setEditingProduct(product);
            setForm({
                name: product.name, sku: product.sku, barcode: product.barcode || '',
                description: product.description || '', category_id: product.category_id,
                price: product.price.toString(), cost_price: product.cost_price.toString(),
                stock_quantity: product.stock_quantity.toString(),
                low_stock_threshold: product.low_stock_threshold.toString(),
                image_url: product.image_url || '', auto_reorder: product.auto_reorder || false,
                reorder_supplier_id: product.reorder_supplier_id || '',
                reorder_quantity: (product.reorder_quantity || 20).toString()
            });
        } else { resetForm(); }
        setDialogOpen(true);
    };

    const handleSaveProduct = async () => {
        if (!form.name || !form.sku || !form.category_id || !form.price || !form.cost_price) {
            toast.error('Please fill all required fields'); return;
        }
        if (form.auto_reorder && !form.reorder_supplier_id) {
            toast.error('Please select a supplier for auto-reorder'); return;
        }
        setSaving(true);
        try {
            const data = { ...form, price: parseFloat(form.price), cost_price: parseFloat(form.cost_price), stock_quantity: parseInt(form.stock_quantity) || 0, low_stock_threshold: parseInt(form.low_stock_threshold) || 10, reorder_quantity: parseInt(form.reorder_quantity) || 20, reorder_supplier_id: form.reorder_supplier_id || null };
            if (editingProduct) { await productsAPI.update(editingProduct.id, data); toast.success('Product updated'); }
            else { await productsAPI.create(data); toast.success('Product created'); }
            setDialogOpen(false); resetForm(); fetchData();
        } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save product'); }
        finally { setSaving(false); }
    };

    const handleDeleteProduct = async (id) => {
        if (!window.confirm('Delete this product?')) return;
        try { await productsAPI.delete(id); toast.success('Product deleted'); fetchData(); }
        catch { toast.error('Failed to delete product'); }
    };

    const handleSaveCategory = async () => {
        if (!categoryForm.name) { toast.error('Category name is required'); return; }
        try {
            await categoriesAPI.create(categoryForm);
            toast.success('Category created');
            setCategoryDialogOpen(false);
            setCategoryForm({ name: '', description: '' });
            fetchData();
        } catch { toast.error('Failed to create category'); }
    };

    const handleBarcodeScan = async (barcode) => {
        setForm(prev => ({ ...prev, barcode }));
        setLookingUp(true);
        setBarcodeLookupResult(null);
        
        try {
            const response = await barcodeAPI.lookup(barcode);
            const data = response.data;
            
            if (data.name) {
                setForm(prev => ({
                    ...prev,
                    name: data.name,
                    description: data.description || prev.description,
                    image_url: data.image_url || prev.image_url,
                    barcode: barcode
                }));
                setBarcodeLookupResult({
                    found: true,
                    source: data.source,
                    name: data.name
                });
                toast.success(`Found: ${data.name}`);
            } else {
                setBarcodeLookupResult({
                    found: false,
                    source: 'not_found'
                });
                toast.info('Product not found. Try a different barcode or enter details manually.');
            }
        } catch (error) {
            console.error('Barcode lookup error:', error);
            toast.error('Failed to look up barcode. Please try again.');
            setBarcodeLookupResult({ found: false });
        } finally {
            setLookingUp(false);
        }
    };

    const getStockColor = (product) => {
        if (product.stock_quantity <= 0) return 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20';
        if (product.stock_quantity <= product.low_stock_threshold) return 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20';
        return 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20';
    };

    const getMarginPct = (p) => {
        if (!p.price || !p.cost_price) return null;
        return Math.round((1 - p.cost_price / p.price) * 100);
    };

    if (loading) {
        return (
            <Layout title="Products">
                <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-20 rounded-xl bg-muted animate-pulse" />)}</div>
                    <div className="h-96 rounded-xl bg-muted animate-pulse" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout title="Products">
            <div className="space-y-5" data-testid="products-page">

                {/* ── Stat Bar ── */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                        { label: 'Total Products', value: products.length, icon: Package, color: 'cyan', border: 'border-l-cyan-500', bg: 'bg-cyan-500/10', text: 'text-cyan-600 dark:text-cyan-400' },
                        { label: 'Categories', value: categories.length, icon: Tag, color: 'violet', border: 'border-l-violet-500', bg: 'bg-violet-500/10', text: 'text-violet-600 dark:text-violet-400' },
                        { label: 'Low Stock', value: lowStockCount, icon: AlertTriangle, color: 'amber', border: 'border-l-amber-500', bg: 'bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400' },
                        { label: 'Inventory Value', value: `$${inventoryValue.toFixed(0)}`, icon: DollarSign, color: 'emerald', border: 'border-l-emerald-500', bg: 'bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400' },
                    ].map(({ label, value, icon: Icon, border, bg, text }) => (
                        <div key={label} className={`stat-card border-l-4 ${border}`}>
                            <div className="flex items-center gap-3">
                                <div className={`stat-card-icon ${bg}`}><Icon className={`h-5 w-5 ${text}`} /></div>
                                <div><p className="text-2xl font-black font-mono">{value}</p><p className="text-xs text-muted-foreground font-medium">{label}</p></div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* ── Toolbar ── */}
                <div className="flex flex-col gap-3">
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input placeholder="Search products..." className="pl-9" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} data-testid="product-search" />
                        </div>
                        <div className="flex items-center border rounded-lg p-1 bg-muted/40 shrink-0">
                            <Button variant={viewMode === 'table' ? 'secondary' : 'ghost'} size="icon" className="h-7 w-7" onClick={() => setViewMode('table')}><LayoutList className="h-3.5 w-3.5" /></Button>
                            <Button variant={viewMode === 'grid' ? 'secondary' : 'ghost'} size="icon" className="h-7 w-7" onClick={() => setViewMode('grid')}><LayoutGrid className="h-3.5 w-3.5" /></Button>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setCategoryDialogOpen(true)} className="shrink-0" data-testid="add-category-btn">
                            <Tag className="h-4 w-4 mr-1.5" /><span className="hidden sm:inline">Category</span>
                        </Button>
                        <Button onClick={() => handleOpenDialog()} className="bg-cyan-500 hover:bg-cyan-600 text-white shrink-0" data-testid="add-product-btn">
                            <Plus className="h-4 w-4 mr-1.5" /> Add
                        </Button>
                    </div>

                    {/* Category tab strip */}
                    <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none" style={{ scrollbarWidth: 'none' }}>
                        {[{ id: 'all', name: 'All' }, ...categories].map((cat) => (
                            <button
                                key={cat.id}
                                onClick={() => setFilterCategory(cat.id)}
                                className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${
                                    filterCategory === cat.id
                                        ? 'bg-cyan-500 text-white border-cyan-500 shadow-sm shadow-cyan-500/25'
                                        : 'border-border text-muted-foreground hover:border-cyan-500/40 hover:text-foreground bg-muted/40'
                                }`}
                            >
                                {cat.name}
                                {cat.id !== 'all' && (
                                    <span className={`ml-1.5 ${filterCategory === cat.id ? 'text-white/70' : 'text-muted-foreground'}`}>
                                        {products.filter(p => p.category_id === cat.id).length}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* ── Grid View ── */}
                {viewMode === 'grid' ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {filteredProducts.map((product) => {
                            const margin = getMarginPct(product);
                            return (
                                <div key={product.id} className="bg-card border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-testid={`product-card-${product.id}`}>
                                    {/* Image */}
                                    <div className="relative aspect-[4/3] bg-muted">
                                        {product.image_url ? (
                                            <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <Package className="h-8 w-8 text-muted-foreground/20" />
                                            </div>
                                        )}
                                        {/* Stock badge */}
                                        <span className={`absolute top-2 left-2 text-[10px] font-bold px-2 py-0.5 rounded-full border ${getStockColor(product)}`}>
                                            {product.stock_quantity <= 0 ? 'Out' : product.stock_quantity <= product.low_stock_threshold ? 'Low' : `${product.stock_quantity}`}
                                        </span>
                                        {/* Margin badge */}
                                        {margin !== null && (
                                            <span className="absolute top-2 right-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-black/60 text-white backdrop-blur-sm">
                                                {margin}%
                                            </span>
                                        )}
                                    </div>
                                    {/* Info */}
                                    <div className="p-3">
                                        <p className="font-semibold text-sm truncate leading-tight">{product.name}</p>
                                        <p className="text-xs text-muted-foreground truncate mt-0.5">{product.category_name}</p>
                                        <p className="font-black font-mono text-base mt-2">${product.price.toFixed(2)}</p>
                                    </div>
                                    {/* Always-visible actions */}
                                    <div className="flex border-t">
                                        <button
                                            onClick={() => handleOpenDialog(product)}
                                            className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs text-muted-foreground hover:text-cyan-600 hover:bg-cyan-500/5 transition-colors"
                                        >
                                            <Pencil className="h-3.5 w-3.5" /> Edit
                                        </button>
                                        <div className="w-px bg-border" />
                                        <button
                                            onClick={() => handleDeleteProduct(product.id)}
                                            className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs text-muted-foreground hover:text-red-600 hover:bg-red-500/5 transition-colors"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" /> Delete
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                        {filteredProducts.length === 0 && (
                            <div className="col-span-full py-20 text-center">
                                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-muted flex items-center justify-center"><Package className="h-7 w-7 text-muted-foreground" /></div>
                                <p className="font-semibold text-muted-foreground">No products found</p>
                                <p className="text-xs text-muted-foreground mt-1">Try adjusting your search or filters</p>
                            </div>
                        )}
                    </div>
                ) : (
                    /* ── List / Table View ── */
                    <Card className="shadow-sm overflow-hidden">
                        <CardContent className="p-0">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                                        <TableHead className="font-semibold">Product</TableHead>
                                        <TableHead className="font-semibold hidden sm:table-cell">Category</TableHead>
                                        <TableHead className="text-right font-semibold">Price</TableHead>
                                        <TableHead className="text-right font-semibold hidden md:table-cell">Margin</TableHead>
                                        <TableHead className="text-right font-semibold hidden md:table-cell">Stock</TableHead>
                                        <TableHead className="w-[50px]" />
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredProducts.map((product) => {
                                        const margin = getMarginPct(product);
                                        return (
                                            <TableRow key={product.id} className="group" data-testid={`product-row-${product.id}`}>
                                                <TableCell>
                                                    <div className="flex items-center gap-3">
                                                        {product.image_url ? (
                                                            <img src={product.image_url} alt={product.name} className="h-10 w-10 rounded-lg object-cover shrink-0" />
                                                        ) : (
                                                            <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                                                                <Package className="h-4 w-4 text-muted-foreground" />
                                                            </div>
                                                        )}
                                                        <div>
                                                            <p className="font-semibold text-sm">{product.name}</p>
                                                            <p className="text-xs font-mono text-muted-foreground">{product.sku}</p>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="hidden sm:table-cell">
                                                    <Badge variant="secondary" className="text-xs">{product.category_name}</Badge>
                                                </TableCell>
                                                <TableCell className="text-right font-mono font-bold">${product.price.toFixed(2)}</TableCell>
                                                <TableCell className="text-right hidden md:table-cell">
                                                    {margin !== null && (
                                                        <span className={`text-xs font-bold font-mono ${margin >= 40 ? 'text-emerald-600' : margin >= 20 ? 'text-amber-600' : 'text-red-500'}`}>
                                                            {margin}%
                                                        </span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-right hidden md:table-cell">
                                                    <Badge className={`text-xs border ${getStockColor(product)}`} variant="outline">
                                                        {product.stock_quantity}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`product-menu-${product.id}`}>
                                                                <MoreVertical className="h-4 w-4" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            <DropdownMenuItem onClick={() => handleOpenDialog(product)}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                                                            <DropdownMenuItem onClick={() => handleDeleteProduct(product.id)} className="text-destructive"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {filteredProducts.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} className="py-16 text-center">
                                                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-muted flex items-center justify-center"><Package className="h-5 w-5 text-muted-foreground" /></div>
                                                <p className="font-semibold text-muted-foreground">No products found</p>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* ── Product Dialog ── */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
                    <DialogHeader>
                        <DialogTitle>{editingProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
                        <DialogDescription>Fill in the product details below</DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6">
                        {/* Section 1: Basic Info */}
                        <div>
                            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">Basic Info</p>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2 space-y-2">
                                    <Label htmlFor="name">Product Name <span className="text-red-500">*</span></Label>
                                    <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Ful Special" data-testid="product-name-input" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="sku">SKU <span className="text-red-500">*</span></Label>
                                    <Input id="sku" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder="ITEM-001" data-testid="product-sku-input" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="barcode">Barcode / UPC</Label>
                                    <div className="flex gap-2">
                                        <Input 
                                            id="barcode" 
                                            value={form.barcode} 
                                            onChange={(e) => setForm({ ...form, barcode: e.target.value })} 
                                            placeholder="Scan or type..."
                                            data-testid="product-barcode-input"
                                            className="flex-1"
                                        />
                                        <Button 
                                            type="button"
                                            variant="outline" 
                                            size="icon"
                                            onClick={() => setScannerOpen(true)}
                                            title="Scan barcode"
                                            data-testid="scan-barcode-btn"
                                        >
                                            <Scan className="h-4 w-4" />
                                        </Button>
                                    </div>
                                    {lookingUp && (
                                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                                            <Loader2 className="h-3 w-3 animate-spin" />
                                            Looking up...
                                        </p>
                                    )}
                                    {barcodeLookupResult?.found && (
                                        <p className="text-xs text-emerald-600 flex items-center gap-1">
                                            <CheckCircle className="h-3 w-3" />
                                            Found via {barcodeLookupResult.source}
                                        </p>
                                    )}
                                    {barcodeLookupResult?.found === false && (
                                        <p className="text-xs text-amber-600">
                                            Not found in database. Enter details manually.
                                        </p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label>Category <span className="text-red-500">*</span></Label>
                                    <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                                        <SelectTrigger data-testid="product-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                                        <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div className="col-span-2 space-y-2">
                                    <Label htmlFor="description">Description</Label>
                                    <Input id="description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="product-description-input" />
                                </div>
                                <div className="col-span-2 space-y-2">
                                    <Label htmlFor="image_url">Image URL</Label>
                                    <Input id="image_url" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." data-testid="product-image-input" />
                                </div>
                            </div>
                        </div>

                        {/* Section 2: Pricing */}
                        <div className="border-t pt-4">
                            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">Pricing</p>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="price">Sell Price <span className="text-red-500">*</span></Label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                                        <Input id="price" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="0.00" className="pl-7" data-testid="product-price-input" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="cost_price">Cost Price <span className="text-red-500">*</span></Label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                                        <Input id="cost_price" type="number" step="0.01" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} placeholder="0.00" className="pl-7" data-testid="product-cost-input" />
                                    </div>
                                </div>
                                {form.price && form.cost_price && parseFloat(form.price) > 0 && (
                                    <div className="col-span-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50 border border-border">
                                        <TrendingUp className="h-4 w-4 text-emerald-500" />
                                        <span className="text-sm text-muted-foreground">Margin:</span>
                                        <span className="font-bold text-sm text-emerald-600">
                                            {Math.round((1 - parseFloat(form.cost_price) / parseFloat(form.price)) * 100)}%
                                            &nbsp;(${(parseFloat(form.price) - parseFloat(form.cost_price)).toFixed(2)} profit)
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Section 3: Inventory & Reorder */}
                        <div className="border-t pt-4">
                            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">Inventory & Reorder</p>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="stock">Stock Quantity</Label>
                                    <Input id="stock" type="number" value={form.stock_quantity} onChange={(e) => setForm({ ...form, stock_quantity: e.target.value })} placeholder="0" data-testid="product-stock-input" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="threshold">Low Stock Alert At</Label>
                                    <Input id="threshold" type="number" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} placeholder="10" data-testid="product-threshold-input" />
                                </div>
                                <div className="col-span-2">
                                    <div className="flex items-center justify-between mb-3">
                                        <div>
                                            <p className="font-semibold text-sm">Auto-Reorder</p>
                                            <p className="text-xs text-muted-foreground">Auto-create purchase order when stock runs low</p>
                                        </div>
                                        <Switch checked={form.auto_reorder} onCheckedChange={(v) => setForm({ ...form, auto_reorder: v })} data-testid="auto-reorder-switch" />
                                    </div>
                                    {form.auto_reorder && (
                                        <div className="grid grid-cols-2 gap-4 pl-4 border-l-2 border-cyan-500">
                                            <div className="space-y-2">
                                                <Label>Supplier <span className="text-red-500">*</span></Label>
                                                <Select value={form.reorder_supplier_id} onValueChange={(v) => setForm({ ...form, reorder_supplier_id: v })}>
                                                    <SelectTrigger data-testid="reorder-supplier-select"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                                                    <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                                                </Select>
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="reorder_qty">Reorder Quantity</Label>
                                                <Input id="reorder_qty" type="number" min="1" value={form.reorder_quantity} onChange={(e) => setForm({ ...form, reorder_quantity: e.target.value })} data-testid="reorder-quantity-input" />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleSaveProduct} disabled={saving} className="bg-cyan-500 hover:bg-cyan-600 text-white" data-testid="save-product-btn">
                            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {editingProduct ? 'Update Product' : 'Create Product'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ── Category Dialog ── */}
            <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
                <DialogContent data-testid="category-dialog">
                    <DialogHeader>
                        <DialogTitle>Add Category</DialogTitle>
                        <DialogDescription>Create a new product category</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="cat-name">Category Name <span className="text-red-500">*</span></Label>
                            <Input id="cat-name" value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} placeholder="e.g. Appetizers" data-testid="category-name-input" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="cat-desc">Description</Label>
                            <Input id="cat-desc" value={categoryForm.description} onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })} data-testid="category-description-input" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleSaveCategory} className="bg-cyan-500 hover:bg-cyan-600 text-white" data-testid="save-category-btn">Create</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* ── Barcode Scanner ── */}
            <BarcodeScanner 
                open={scannerOpen} 
                onOpenChange={setScannerOpen} 
                onScan={handleBarcodeScan}
            />
        </Layout>
    );
}
